/**
 * Vocabulario de dominio compartido entre backend y frontend.
 * Fuente única de verdad: la base de datos usa estos mismos literales.
 */

export const ROLES = ["admin", "operator", "reviewer", "viewer"] as const;
export type Role = (typeof ROLES)[number];

/** Estados primarios de la máquina de estados de misión. */
export const MISSION_STATUSES = [
  "pending",
  "planning",
  "awaiting_plan_approval",
  "developing",
  "awaiting_ci",
  "in_review", // = esperando aprobación de entrega
  "merging", // entrega aprobada; esperando merge (API o manual en GitHub)
  "done",
  "blocked",
  "error",
  "awaiting_budget",
  "cancelled",
] as const;
export type MissionStatus = (typeof MISSION_STATUSES)[number];

/** Estados "secundarios": se muestran como indicador dentro de una columna del Kanban. */
export const SECONDARY_STATUSES = ["blocked", "error", "awaiting_budget", "cancelled"] as const;
export type SecondaryStatus = (typeof SECONDARY_STATUSES)[number];

/** Columnas visibles del Kanban. */
export const STAGES = ["pending", "planning", "development", "review", "pr_ci", "done"] as const;
export type Stage = (typeof STAGES)[number];

/** Indicadores que se pintan dentro de la columna (no hay columna por excepción). */
export const INDICATORS = [
  "awaiting_plan_approval",
  "awaiting_delivery_approval",
  "awaiting_ci",
  "blocked",
  "error",
  "cancelled",
  "awaiting_budget",
] as const;
export type Indicator = (typeof INDICATORS)[number];

export const FLOWS = ["fast_track", "supervised", "restricted"] as const;
export type Flow = (typeof FLOWS)[number];

/** Orden creciente de severidad. `unknown` se trata como restricted. */
export const RISK_LEVELS = ["minimal", "standard", "high", "unknown"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

/**
 * Perfil de ejecución (Fase 2): cuánto trabajo, qué instrucciones y qué recursos. Es una dimensión DISTINTA del flujo
 * (quién aprueba): ningún perfil cambia el riesgo, el flujo, los permisos ni las verificaciones obligatorias.
 * Orden creciente de profundidad: Ligero < Estándar < Profundo.
 */
export const EXECUTION_PROFILES = ["light", "standard", "deep"] as const;
export type ExecutionProfile = (typeof EXECUTION_PROFILES)[number];
export const profileRank = (p: ExecutionProfile): number => EXECUTION_PROFILES.indexOf(p);
export const maxProfile = (...ps: ExecutionProfile[]): ExecutionProfile => ps.reduce<ExecutionProfile>((a, b) => (profileRank(b) > profileRank(a) ? b : a), "light");

export const PRIORITIES = ["high", "medium", "low"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const CHANGE_CLASSES = ["typo", "static_copy", "docs", "trivial_ui", "test_tweak", "other"] as const;
export type ChangeClass = (typeof CHANGE_CLASSES)[number];

export const RUN_STATUSES = ["creating", "running", "waiting", "finished", "error", "cancelled", "expired"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const ACTIVE_RUN_STATUSES: readonly RunStatus[] = ["creating", "running", "waiting"];

export const RUN_PURPOSES = ["plan", "develop", "fix", "followup"] as const;
export type RunPurpose = (typeof RUN_PURPOSES)[number];

export const APPROVAL_KINDS = ["plan", "delivery", "budget_extension", "second_approver", "scope"] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

export const APPROVAL_DECISIONS = ["approved", "changes_requested", "rejected"] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

export const RISK_PHASES = ["creation", "planning", "diff", "manual"] as const;
export type RiskPhase = (typeof RISK_PHASES)[number];

export const NOTIFICATION_CATEGORIES = [
  "plan_pending",
  "delivery_pending",
  "mission_blocked",
  "agent_failed",
  "ci_failed",
  "budget_exceeded",
  "mission_done",
  "merge_completed",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const PR_STATES = ["open", "closed", "merged"] as const;
export type PrState = (typeof PR_STATES)[number];

export const DATA_CLASSES = ["none", "personal", "sensitive"] as const;
export type DataClass = (typeof DATA_CLASSES)[number];

// ---------- helpers puros ----------

export const riskRank = (l: RiskLevel): number => RISK_LEVELS.indexOf(l);
export const maxRisk = (...levels: RiskLevel[]): RiskLevel =>
  levels.reduce<RiskLevel>((a, b) => (riskRank(b) > riskRank(a) ? b : a), "minimal");

export const flowForRisk = (l: RiskLevel): Flow =>
  l === "minimal" ? "fast_track" : l === "standard" ? "supervised" : "restricted";

/** Mapeo estado → columna del Kanban. Los secundarios heredan la columna donde estaban. */
export function stageOf(status: MissionStatus, resumeStatus?: MissionStatus | null): Stage {
  switch (status) {
    case "pending":
      return "pending";
    case "planning":
    case "awaiting_plan_approval":
      return "planning";
    case "developing":
      return "development";
    case "in_review":
      return "review";
    case "awaiting_ci":
    case "merging":
      return "pr_ci";
    case "done":
      return "done";
    case "blocked":
    case "error":
    case "awaiting_budget":
    case "cancelled":
      return resumeStatus && !(SECONDARY_STATUSES as readonly string[]).includes(resumeStatus)
        ? stageOf(resumeStatus)
        : "pending";
  }
}

export function indicatorOf(status: MissionStatus): Indicator | null {
  switch (status) {
    case "awaiting_plan_approval":
      return "awaiting_plan_approval";
    case "in_review":
      return "awaiting_delivery_approval";
    case "awaiting_ci":
      return "awaiting_ci";
    case "blocked":
    case "error":
    case "cancelled":
    case "awaiting_budget":
      return status;
    default:
      return null;
  }
}

export const TERMINAL_STATUSES: readonly MissionStatus[] = ["done", "cancelled"];
export const isTerminal = (s: MissionStatus) => TERMINAL_STATUSES.includes(s);
